import React, { useEffect, useState } from "react";
import { api, ApiError } from "../../api";
import { useAuth } from "../../AuthContext";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";

// Shown on the dashboard until the account's email address is verified. The
// app stays fully usable; this just asks for the 6-digit code we email.
export function VerifyEmailNotice() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // emailVerified is only sent by an up-to-date server, so an older one
  // (undefined) never shows the banner
  if (!user || user.emailVerified !== false) return null;
  return (
    <>
      <div className="card" style={{ marginBottom: 14, borderColor: "var(--cash)", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <b style={{ fontSize: 13 }}>{t("verifyEmail.bannerTitle")}</b>
          <div className="sub" style={{ fontSize: 12 }}>{t("verifyEmail.bannerBody", { email: user.email })}</div>
        </div>
        <button className="btn-solid btn-sm" onClick={() => setOpen(true)}>{t("verifyEmail.verifyBtn")}</button>
      </div>
      {open && <VerifyEmailSheet email={user.email} onClose={() => setOpen(false)} />}
    </>
  );
}

function VerifyEmailSheet({ email, onClose }: { email: string; onClose: () => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const { refresh } = useAuth();
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const id = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(id);
  }, [wait]);

  async function sendCode() {
    setSending(true);
    try {
      const r = await api.post("/api/auth/email/send-code");
      if (r?.verified) {
        await refresh();
        onClose();
        return;
      }
      setSentTo(r?.sentTo ?? email);
      setWait(60);
    } catch (err) {
      // "wait a minute" means a code was just sent: carry on to the code box
      if (err instanceof ApiError && err.status === 429) setSentTo((s) => s ?? email);
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setSending(false);
    }
  }

  async function verify() {
    setChecking(true);
    try {
      await api.post("/api/auth/email/verify", { code });
      toast(t("verifyEmail.verifiedToast"));
      await refresh();
      onClose();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setChecking(false);
    }
  }

  return (
    <>
      <div className="filter-backdrop" onClick={onClose} />
      <div className="filter-sheet" style={{ textAlign: "center" }}>
        <div className="filter-sheet-handle" />
        <div className="reg-title" style={{ marginBottom: 6 }}>{t("verifyEmail.sheetTitle")}</div>
        {!sentTo ? (
          <>
            <div className="sub" style={{ marginBottom: 18 }}>{t("verifyEmail.sendIntro", { email })}</div>
            <button className="btn-solid" disabled={sending} onClick={sendCode}>{sending ? t("verifyEmail.sending") : t("verifyEmail.sendBtn")}</button>
          </>
        ) : (
          <>
            <div className="sub" style={{ marginBottom: 14 }}>{t("verifyEmail.sentTo", { email: sentTo })}</div>
            <div className="field">
              <div className="input-wrap">
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="------"
                  style={{ textAlign: "center", fontSize: 28, letterSpacing: "0.4em", fontWeight: 700 }}
                />
              </div>
            </div>
            <button className="btn-solid" disabled={code.length !== 6 || checking} onClick={verify}>{checking ? t("verifyEmail.checking") : t("verifyEmail.confirmBtn")}</button>
            <div>
              <button type="button" className="link-btn" style={{ marginTop: 12 }} disabled={wait > 0 || sending} onClick={sendCode}>
                {wait > 0 ? t("verifyEmail.resendIn", { seconds: wait }) : t("verifyEmail.resendBtn")}
              </button>
            </div>
          </>
        )}
        <div><button type="button" className="link-btn" style={{ marginTop: 6 }} onClick={onClose}>{t("verifyEmail.later")}</button></div>
      </div>
    </>
  );
}
